import { Loader } from '@googlemaps/js-api-loader';
import { APP, calcFare, getDefaultZone } from '../config/app';
import { logAnalyticsEvent } from './firebase';
import { ensureLocationPermission } from '../utils/locationPermission';
import { chooseRouteByVehicle, toDirectionsRoute } from '../utils/routeSelection';
import { predictionText, predictionMainText } from '../utils/predictionText';
import { isAreaPlace } from '../utils/placeKind';
import { autocompleteSearchMode } from '../utils/autocompleteMode';
import { trackServiceAreaCircle, syncServiceAreaCircles } from './serviceAreaCircles';
import { findZone, gpsMayChangeTown, withTownContext } from '../utils/zones';
import { emitZoneChange } from './zoneEvents';
import { Capacitor } from '@capacitor/core';
import { shouldUseNativeMap, NATIVE_ICONS, toLatLngLiteral } from './nativeMapUtils';

/**
 * Native permission gate — rejects with Error('denied') when the OS permission
 * is permanently denied, so callers surface `book.geoDenied` instead of hanging
 * on a `navigator.geolocation` call the WebView will silently refuse. No-op on
 * the web (the browser shows its own prompt).
 */
const assertLocationPermission = async ({ requestPermission = true } = {}) => {
  // Absolute outer ceiling, on top of every inner timeout already inside
  // ensureLocationPermission (locationPermission.js). Whatever happens in
  // there — including a hang neither of us has found yet — this call must
  // still settle, so it can never freeze the caller (cold-start pickup
  // detection, or the recenter button) forever.
  const ceilingMs = requestPermission ? 25000 : 12000;
  let state;
  try {
    state = await Promise.race([
      ensureLocationPermission({ request: requestPermission }),
      new Promise((resolve) => setTimeout(() => resolve('__timeout__'), ceilingMs)),
    ]);
  } catch {
    // Never hard-block here on an unexpected error — fall through and let
    // navigator.geolocation itself (with its own independent timeout) be
    // the final word.
    state = 'granted';
  }
  if (state === 'denied') throw new Error('denied');
};

let mapsPromise = null;

export const SERVICE_RADIUS_M = () => APP.serviceRadiusKm * 1000;

export const getServiceAreaBounds = (google) => {
  const b = circleBounds(APP.mapCenter, APP.serviceRadiusKm);
  return new google.maps.LatLngBounds(
    { lat: b.south, lng: b.west },
    { lat: b.north, lng: b.east },
  );
};

export const getServiceAreaBias = () => ({
  radius: APP.serviceRadiusKm * 1000,
  center: APP.mapCenter,
});

export const loadGoogleMaps = () => {
  if (mapsPromise) return mapsPromise;
  const key = process.env.REACT_APP_GOOGLE_MAPS_KEY || process.env.REACT_APP_FIREBASE_API_KEY;
  if (!key) return Promise.reject(new Error('Missing Google Maps API key'));

  const loader = new Loader({
    apiKey: key,
    version: 'quarterly',
    region: 'IN',
    language: 'en',
    libraries: ['places', 'marker'],
    authReferrerPolicy: 'origin',
  });

  mapsPromise = loader.load();
  return mapsPromise;
};

/** Great-circle distance in metres (no Maps API needed). */
export const distanceBetweenM = (a, b) => {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

export const distanceFromCenterM = (lat, lng) =>
  distanceBetweenM(APP.mapCenter, { lat, lng });

/**
 * Which zone (if any) a point falls in — mirrors rideSecurity.js's
 * server-side resolveZone exactly (same "default zone first, then extra
 * towns" order) so the client's map/messaging always agrees with what the
 * server will actually accept a booking in. Pure query, no side effects.
 */
export const resolveZone = (lat, lng) => findZone(
  [
    getDefaultZone(),
    ...APP.zones,
  ],
  lat, lng,
);

export const getServiceZones = ({ activeOnly = false } = {}) => {
  const zones = [getDefaultZone(), ...APP.zones];
  return activeOnly ? zones.filter((zone) => zone.active !== false) : zones;
};

/** Point the passenger UI at a configured town without needing a GPS fix. */
export const applyZoneById = (zoneId, { manual = false } = {}) => {
  const zone = getServiceZones().find((item) => item.id === zoneId);
  if (!zone) return null;
  APP.currentZoneId = zone.id;
  APP.currentZoneActive = zone.active !== false;
  APP.city = zone.name;
  APP.mapCenter = { lat: zone.center.lat, lng: zone.center.lng };
  APP.serviceRadiusKm = zone.radiusKm;
  if (manual) APP.zoneManuallySelected = true;
  syncServiceAreaCircles();
  emitZoneChange(zone);
  return zone;
};

/**
 * Called once Book gets a real GPS fix (cold open, or a manual recenter).
 * Points every existing APP.city/mapCenter/serviceRadiusKm reader (the map,
 * the "outside area" toasts, SOS/support messages, ...) at whichever zone
 * the passenger is actually standing in, with zero changes needed at any of
 * those call sites. A point outside every zone leaves APP as it was —
 * same "outside service area" behaviour as before zones existed.
 */
export const applyDetectedZone = (lat, lng, { explicit = false } = {}) => {
  if (!gpsMayChangeTown({ pickedThisSession: APP.zoneManuallySelected, explicit })) return null;
  const zone = resolveZone(lat, lng);
  if (!zone) return null;
  return applyZoneById(zone.id, { manual: explicit });
};

/** Sync — no Maps API round-trip. A zone that exists but isn't live yet ("coming soon") never counts as bookable. */
export const isWithinServiceArea = (lat, lng) =>
  APP.currentZoneActive !== false && distanceFromCenterM(lat, lng) <= APP.serviceRadiusKm * 1000;

let geocoderInstance = null;
let directionsServiceInstance = null;

// Spatial distance reverse-geocode cache (hit within 6 meters)
const spatialGeocodeCache = [];
const GEOCODE_CACHE_MAX = 80;
const SPATIAL_CACHE_HIT_RADIUS_M = 6;

const findSpatialGeocodeCache = (lat, lng) => {
  const target = { lat, lng };
  for (let i = spatialGeocodeCache.length - 1; i >= 0; i--) {
    const entry = spatialGeocodeCache[i];
    if (distanceBetweenM(target, entry) <= SPATIAL_CACHE_HIT_RADIUS_M) {
      return entry.place;
    }
  }
  return null;
};

const saveSpatialGeocodeCache = (lat, lng, place) => {
  if (spatialGeocodeCache.length >= GEOCODE_CACHE_MAX) {
    spatialGeocodeCache.shift();
  }
  spatialGeocodeCache.push({ lat, lng, place });
};

// Short-term directions cache (60s TTL) to prevent repeated billing on tab changes
const directionsCache = new Map();
const DIRECTIONS_CACHE_MAX = 20;
// A cache only helps after a request completes. Book, active-trip, and a
// remount can otherwise ask for the same route while the first request is
// still in flight, which creates duplicate paid Directions calls. Share that
// one request; successes still enter the normal short-lived result cache.
const directionsInFlight = new Map();
const geocodeInFlight = new Map();

const getDirectionsCacheKey = (o, d) =>
  `${o.lat.toFixed(4)},${o.lng.toFixed(4)}->${d.lat.toFixed(4)},${d.lng.toFixed(4)}`;

const getGoogle = () => window.google?.maps ? window.google : null;

export const DAY_MAP_STYLE = [
  { elementType: 'geometry', stylers: [{ color: '#f8fafc' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#475569' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#ffffff' }, { weight: 3 }] },
  { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#cbd5e1' }] },
  { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#f1f5f9' }] },
  { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#e2e8f0' }] },
  { featureType: 'poi', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#64748b' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#dcfce7' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#e2e8f0' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#64748b' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
  { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#cbd5e1' }] },
  { featureType: 'transit', elementType: 'geometry', stylers: [{ color: '#f1f5f9' }] },
  { featureType: 'transit.station', elementType: 'labels.icon', stylers: [{ visibility: 'on' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#e0f2fe' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#0284c7' }] },
];

export const NIGHT_MAP_STYLE = [
  { elementType: 'geometry', stylers: [{ color: '#0B1120' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#94A3B8' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#0B1120' }, { weight: 3 }] },
  { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#334155' }] },
  { featureType: 'administrative.land_parcel', elementType: 'labels.text.fill', stylers: [{ color: '#64748B' }] },
  { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#0F172A' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#1E293B' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#0F172A' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#94A3B8' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#1E3A8A' }] },
  { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#172554' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#070D18' }] },
];

export const isNightMode = () => false;

/**
 * True when a screen that opts in (createMap's `allowNative`) should get the
 * native Android map. See nativeMapUtils.shouldUseNativeMap.
 */
export const isNativeMapEnabled = () => shouldUseNativeMap({
  isNative: Capacitor.isNativePlatform(),
  pluginAvailable: Capacitor.isPluginAvailable('CapacitorGoogleMaps'),
  flag: process.env.REACT_APP_NATIVE_MAPS,
});

/**
 * Screens opt in one at a time with `allowNative: true`. A native map must be
 * released with `destroyMap(map)` when the screen unmounts. If the native map
 * can't be created, this falls back to the JS map so the screen still works.
 */
export const createMap = async (container, center = APP.mapCenter, options = {}) => {
  if (options.allowNative && isNativeMapEnabled()) {
    try {
      const { createNativeMap } = await import('./nativeMap');
      const map = await createNativeMap(container, center, {
        zoom: Number.isFinite(Number(options.zoom)) ? Number(options.zoom) : 14,
        styles: DAY_MAP_STYLE,
        mapId: process.env.REACT_APP_GOOGLE_MAPS_ANDROID_MAP_ID || '',
        // A map opened on top of another page (a full-screen picker): that
        // page is hidden while it is open (nativeMapLayers.js).
        overlayRoot: options.nativeOverlayRoot || null,
      });
      if (options.showServiceArea !== false) {
        trackServiceAreaCircle(map.addCircle({
          center: APP.mapCenter,
          radius: APP.serviceRadiusKm * 1000,
          fillColor: '#00A86B',
          fillOpacity: 0.04,
          strokeColor: '#00A86B',
          strokeOpacity: 0.25,
          strokeWeight: 1.5,
          clickable: false,
        }));
      }
      return map;
    } catch (e) {
      console.warn('[maps] native map failed, using the JS map', e);
    }
  }
  const google = await loadGoogleMaps();
  const mapId = process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || undefined;
  const showServiceArea = options.showServiceArea !== false;
  const mapOptions = {
    center,
    zoom: Number.isFinite(Number(options.zoom)) ? Number(options.zoom) : 14,
    disableDefaultUI: true,
    zoomControl: false,
    gestureHandling: 'greedy',
    clickableIcons: true,
    heading: 0,
    isFractionalZoomEnabled: true,
  };

  // Intertown's custom picker must be able to move beyond Pileru, while Book
  // retains its normal service-area map. The India bounds are UI guardrails;
  // server validation remains the authority for every custom quote.
  if (options.restriction) mapOptions.restriction = options.restriction;

  if (mapId) {
    mapOptions.mapId = mapId;
  } else {
    mapOptions.styles = DAY_MAP_STYLE;
  }

  const map = new google.maps.Map(container, mapOptions);

  if (showServiceArea) {
    const circle = new google.maps.Circle({
      map,
      center: APP.mapCenter,
      radius: APP.serviceRadiusKm * 1000,
      fillColor: '#00A86B',
      fillOpacity: 0.04,
      strokeColor: '#00A86B',
      strokeOpacity: 0.25,
      strokeWeight: 1.5,
      clickable: false,
    });
    trackServiceAreaCircle(circle);
  }

  return map;
};

/** Releases a map from createMap. Needed for a native map; a no-op for the JS map. */
export const destroyMap = (map) => {
  if (map?.__native) map.destroy().catch?.(() => {});
};

function circleBounds(center, radiusKm) {
  const latDelta = radiusKm / 111;
  const lngDelta = radiusKm / (111 * Math.cos((center.lat * Math.PI) / 180));
  return {
    north: center.lat + latDelta,
    south: center.lat - latDelta,
    east: center.lng + lngDelta,
    west: center.lng - lngDelta,
  };
}

const LOCATION_TYPE_RANK = { ROOFTOP: 0, RANGE_INTERPOLATED: 1, GEOMETRIC_CENTER: 2, APPROXIMATE: 3 };

/** Prefer street / neighborhood results over bare locality (e.g. "Pileru"). */
const resultTypeScore = (result) => {
  const types = result?.types || [];
  if (types.includes('street_address') || types.includes('premise')) return 0;
  if (types.includes('route') || types.includes('intersection')) return 1;
  if (types.includes('neighborhood') || types.includes('sublocality_level_1') || types.includes('sublocality')) return 2;
  if (types.includes('plus_code')) return 3;
  if (types.includes('locality') || types.includes('administrative_area_level_2')) return 8;
  if (types.includes('administrative_area_level_1') || types.includes('country')) return 9;
  return 5;
};

const pickBestGeocodeResult = (results) => {
  if (!results?.length) return null;
  return [...results].sort((a, b) => {
    const typeDiff = resultTypeScore(a) - resultTypeScore(b);
    if (typeDiff !== 0) return typeDiff;
    const ra = LOCATION_TYPE_RANK[a.geometry?.location_type] ?? 9;
    const rb = LOCATION_TYPE_RANK[b.geometry?.location_type] ?? 9;
    return ra - rb;
  })[0];
};

const componentName = (components, type) =>
  components?.find((c) => c.types.includes(type))?.long_name || '';

const isCoarseLocalityLabel = (label, components) => {
  const s = String(label || '').trim().toLowerCase();
  if (!s) return true;
  const locality = componentName(components, 'locality').toLowerCase();
  const admin2 = componentName(components, 'administrative_area_level_2').toLowerCase();
  return Boolean((locality && s === locality) || (admin2 && s === admin2));
};

/** Trim state/country noise from formatted address for a usable short label. */
const trimFormattedAddress = (formatted) => {
  const parts = String(formatted || '')
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);
  if (!parts.length) return '';
  // Drop trailing country / state / PIN when present
  const cleaned = parts.filter((p, i) => {
    if (/^\d{6}$/.test(p)) return false;
    if (/^india$/i.test(p)) return false;
    if (i === parts.length - 1 && /pradesh|state/i.test(p)) return false;
    if (i >= 1 && /andhra pradesh/i.test(p)) return false;
    return true;
  });
  if (cleaned.length >= 2) return cleaned.slice(0, 2).join(', ');
  return cleaned[0] || parts.slice(0, Math.min(2, parts.length)).join(', ');
};

const parseGeocodeResult = (top, lat, lng) => {
  const components = top.address_components || [];
  const streetNumber = componentName(components, 'street_number');
  const route = componentName(components, 'route');
  const premise = componentName(components, 'premise');
  const neighborhood = componentName(components, 'neighborhood')
    || componentName(components, 'sublocality_level_1')
    || componentName(components, 'sublocality')
    || componentName(components, 'sublocality_level_2');
  const landmark = componentName(components, 'point_of_interest')
    || componentName(components, 'establishment');

  let short = '';
  if (landmark) short = landmark;
  else if (premise && route) short = `${premise}, ${route}`;
  else if (premise) short = premise;
  else if (streetNumber && route) short = `${streetNumber} ${route}`;
  else if (route) short = neighborhood ? `${route}, ${neighborhood}` : route;
  else if (neighborhood) short = neighborhood;
  else short = trimFormattedAddress(top.formatted_address) || top.formatted_address;

  // Never leave passengers with only the town name when a fuller address exists.
  if (isCoarseLocalityLabel(short, components) && top.formatted_address) {
    short = trimFormattedAddress(top.formatted_address) || top.formatted_address;
  }

  return {
    address: top.formatted_address || short,
    name: short,
    lat,
    lng,
  };
};

/** Prefer a specific label for trip cards; avoid lone town names when address is richer. */
export const formatPlaceLabel = (place, maxLen = 48) => {
  if (!place) return '';
  const name = String(place.name || '').trim();
  const address = String(place.address || '').trim();
  let label = name || address;
  if (name && address && name !== address) {
    const nameLooksCoarse = name.split(',').length === 1 && name.length <= 18;
    if (nameLooksCoarse && address.length > name.length + 4) label = address;
  }
  if (!label) return '';
  return label.length > maxLen ? `${label.slice(0, maxLen - 1)}…` : label;
};

/** Place Details (New) by place id — the fallback when toPlace() itself misbehaves. */
const getPlaceDetailsById = async (placeId) => {
  const google = getGoogle() || await loadGoogleMaps();
  await google.maps.importLibrary('places');
  const place = new google.maps.places.Place({ id: placeId });
  await place.fetchFields({ fields: ['displayName', 'formattedAddress', 'location'] });
  const loc = extractCoord(place.location);
  if (!loc) throw new Error('place_details_no_location');
  return {
    address: place.formattedAddress || place.displayName || '',
    name: place.displayName || place.formattedAddress || '',
    lat: loc.lat,
    lng: loc.lng,
  };
};

export const forwardGeocode = async (query, { localContext = true, restrictToServiceArea = true } = {}) => {
  let cleaned = String(query || '').trim();
  if (!cleaned) throw new Error('empty_query');

  // Add the town on screen (APP.city follows the rider's zone) so a bare
  // "bus stand" resolves in that town. Skipped when the query already names
  // it ("Pileru" contains "Piler").
  if (localContext) {
    cleaned = withTownContext(cleaned, APP.city, APP.state);
  }

  const google = await loadGoogleMaps();
  if (!geocoderInstance) geocoderInstance = new google.maps.Geocoder();

  return new Promise((resolve, reject) => {
    const request = {
      address: cleaned,
      region: 'in',
      componentRestrictions: { country: 'in' },
    };
    if (restrictToServiceArea) {
      request.componentRestrictions.administrativeArea = 'Andhra Pradesh';
      request.bounds = getServiceAreaBounds(google);
    }
    geocoderInstance.geocode(
      request,
      (results, status) => {
        if (status !== 'OK' || !results?.[0]) {
          reject(new Error('geocode_failed'));
          return;
        }
        const top = pickBestGeocodeResult(results);
        const loc = top.geometry?.location;
        if (!loc) {
          reject(new Error('geocode_no_location'));
          return;
        }
        const lat = typeof loc.lat === 'function' ? loc.lat() : loc.lat;
        const lng = typeof loc.lng === 'function' ? loc.lng() : loc.lng;
        resolve(parseGeocodeResult(top, lat, lng));
      },
    );
  });
};

export const extractCoord = (locObj) => {
  if (!locObj) return null;
  let lat;
  let lng;
  if (typeof locObj.lat === 'function') {
    lat = locObj.lat();
    lng = locObj.lng();
  } else if (locObj.lat != null && locObj.lng != null) {
    lat = locObj.lat;
    lng = locObj.lng;
  } else if (locObj.latitude != null && locObj.longitude != null) {
    lat = locObj.latitude;
    lng = locObj.longitude;
  }
  lat = Number(lat);
  lng = Number(lng);
  return (!Number.isNaN(lat) && !Number.isNaN(lng)) ? { lat, lng } : null;
};

export const resolvePlaceFromPrediction = async (placePrediction, { restrictToServiceArea = true } = {}) => {
  const placeId = placePrediction?.placeId;
  const label = predictionText(placePrediction);

  // Primary: Place Details (New). `fields` must contain only real Place fields —
  // an unknown one rejects the whole call, which silently drops every search
  // onto the geocode fallback below.
  try {
    const place = placePrediction.toPlace();
    await place.fetchFields({ fields: ['displayName', 'formattedAddress', 'location'] });
    const loc = extractCoord(place.location);
    if (loc) {
      return {
        address: place.formattedAddress || place.displayName || label,
        name: place.displayName || place.formattedAddress || label,
        lat: loc.lat,
        lng: loc.lng,
      };
    }
  } catch (e) {
    console.warn('Place fetchFields failed, trying fallback:', e?.message || e);
  }

  if (placeId) {
    try {
      return await getPlaceDetailsById(placeId);
    } catch (e) {
      console.warn('Place details by id failed:', e?.message || e);
    }
  }

  // Last resort: geocode the place *name* only. The full prediction text carries
  // the street/area tail, which the geocoder resolves to the middle of that
  // road. A coarse match that lands outside the service area is not usable.
  const query = predictionMainText(placePrediction) || label;
  if (query) {
    const geo = await forwardGeocode(query, {
      localContext: restrictToServiceArea,
      restrictToServiceArea,
    });
    if (geo && (!restrictToServiceArea || isWithinServiceArea(geo.lat, geo.lng))) return geo;
    throw new Error('place_resolve_failed');
  }
  throw new Error('place_resolve_failed');
};

/**
 * PlaceAutocompleteElement. Local Book callers retain the default Pileru
 * restriction; Intertown can opt into India-only discovery without weakening
 * Book's location safety boundary.
 */
export const mountPlaceAutocomplete = async (container, placeholder, onPlace, options = {}) => {
  const google = await loadGoogleMaps();
  await google.maps.importLibrary('places');
  // utils/autocompleteMode.js decides how the search is limited. `searchBeyondArea` (Book's drop search
  // when Intertown can take an out-of-area trip) searches by preference instead of by restriction;
  // the selection below is still checked against the service area either way.
  const search = autocompleteSearchMode(options, APP.mapCenter);
  const { restrictToServiceArea } = search;
  const elementOptions = { includedRegionCodes: ['in'] };
  if (search.mode === 'restrict') {
    const bounds = circleBounds(APP.mapCenter, APP.serviceRadiusKm);
    elementOptions.locationRestriction = new google.maps.LatLngBounds(
      { lat: bounds.south, lng: bounds.west },
      { lat: bounds.north, lng: bounds.east },
    );
  } else if (search.bias) {
    elementOptions.locationBias = search.bias;
  }

  const el = new google.maps.places.PlaceAutocompleteElement(elementOptions);
  if (placeholder) el.placeholder = placeholder;
  el.style.width = '100%';
  el.style.colorScheme = 'light';

  el.addEventListener('gmp-select', async ({ placePrediction }) => {
    try {
      if (document.activeElement && typeof document.activeElement.blur === 'function') {
        document.activeElement.blur();
      }
    } catch { /* */ }

    try {
      const point = await resolvePlaceFromPrediction(placePrediction, { restrictToServiceArea });
      if (restrictToServiceArea && !isWithinServiceArea(point.lat, point.lng)) {
        // The place goes along, so a caller can offer something better than an error.
        onPlace(null, 'outsideArea', { place: point });
        return;
      }
      // Passed beside the place, not inside it: callers store the place object.
      onPlace(point, undefined, { isArea: isAreaPlace(placePrediction?.types) });
    } catch (e) {
      console.error('Place select failed:', e);
      onPlace(null, isBillingError(e?.message) ? 'billingError' : 'placeError');
    }
  });

  container.replaceChildren(el);
  return el;
};

// Two-wheeler routes from the Routes library, reshaped to the Directions shape.
// Never throws: an empty list means "use the car route instead" (library or
// travel mode unavailable, API not enabled on the key, no route found).
const TWO_WHEELER_TIMEOUT_MS = 6000;

const getTwoWheelerRoutes = (google, origin, destination) => Promise.race([
  fetchTwoWheelerRoutes(google, origin, destination),
  new Promise((resolve) => setTimeout(() => resolve([]), TWO_WHEELER_TIMEOUT_MS)),
]);

const fetchTwoWheelerRoutes = async (google, origin, destination) => {
  try {
    const { Route } = await google.maps.importLibrary('routes');
    const { routes } = await Route.computeRoutes({
      origin: { lat: origin.lat, lng: origin.lng },
      destination: { lat: destination.lat, lng: destination.lng },
      travelMode: 'TWO_WHEELER',
      computeAlternativeRoutes: true,
      fields: ['path', 'distanceMeters', 'durationMillis'],
    });
    return (routes || [])
      .map((r) => toDirectionsRoute(r, (lat, lng) => new google.maps.LatLng(lat, lng)))
      .filter(Boolean);
  } catch {
    return [];
  }
};

const getCarRoutes = (google, origin, destination) => {
  if (!directionsServiceInstance) {
    directionsServiceInstance = new google.maps.DirectionsService();
  }
  return new Promise((resolve, reject) => {
    directionsServiceInstance.route(
      {
        origin: { lat: origin.lat, lng: origin.lng },
        destination: { lat: destination.lat, lng: destination.lng },
        travelMode: google.maps.TravelMode.DRIVING,
        provideRouteAlternatives: true,
      },
      (result, status) => {
        if (status !== 'OK' || !result?.routes?.[0]) {
          reject(new Error('Could not calculate route'));
          return;
        }
        resolve(result.routes);
      },
    );
  });
};

export const getDirections = (origin, destination) => {
  const cacheKey = getDirectionsCacheKey(origin, destination);
  const cached = directionsCache.get(cacheKey);
  if (cached && (Date.now() - cached.time < 60000)) {
    return Promise.resolve(cached.data);
  }
  const existing = directionsInFlight.get(cacheKey);
  if (existing) return existing;

  const request = loadGoogleMaps().then(async (google) => {
    // An auto is neither a car nor a bike. Google closes some lanes to cars that
    // autos use (a Piler shortcut: 2.9 km, against a 6.1 km car detour) and opens
    // some to a car or bike that an auto cannot take. So ask for both and use the
    // car route unless the two-wheeler route saves a lot (chooseRouteByVehicle).
    // Either lookup may fail on its own; the other still prices the ride.
    const [bikeRoutes, carRoutes] = await Promise.all([
      getTwoWheelerRoutes(google, origin, destination),
      getCarRoutes(google, origin, destination).catch(() => []),
    ]);

    const route = chooseRouteByVehicle(carRoutes, bikeRoutes);
    const leg = route?.legs?.[0];
    if (!route || !leg) throw new Error('Could not calculate route');

    // Trust Google's own route unconditionally — no straight-line-ratio
    // guess-and-override. A ratio-based sanity check sounds safe but
    // isn't: real Piler routes forced around a rail crossing, a one-way
    // system, or a specific building's access road can legitimately run
    // 3-4x+ the straight-line distance, especially on short trips, and
    // there is no way to tell that apart from a genuinely bad route
    // using distance alone. The map, the shown distance/duration and the
    // fare all come straight from the Directions result.
    const distanceKm = (leg.distance?.value || 0) / 1000;
    const durationMin = Math.ceil((leg.duration?.value || 0) / 60);

    const data = {
      distanceKm,
      distanceText: leg.distance?.text || `${distanceKm.toFixed(1)} km`,
      durationMin,
      durationText: leg.duration?.text || `${durationMin} min`,
      fare: calcFare(distanceKm),
      polyline: route.overview_path,
    };
    if (directionsCache.size >= DIRECTIONS_CACHE_MAX) {
      const firstKey = directionsCache.keys().next().value;
      directionsCache.delete(firstKey);
    }
    directionsCache.set(cacheKey, { data, time: Date.now() });
    return data;
  });
  directionsInFlight.set(cacheKey, request);
  request.finally(() => {
    if (directionsInFlight.get(cacheKey) === request) directionsInFlight.delete(cacheKey);
  }).catch(() => {});
  return request;
};

export const reverseGeocode = async (lat, lng) => {
  const cached = findSpatialGeocodeCache(lat, lng);
  if (cached) return { ...cached, lat, lng };

  const cacheKey = `${Number(lat).toFixed(5)},${Number(lng).toFixed(5)}`;
  const existing = geocodeInFlight.get(cacheKey);
  if (existing) return existing;

  const request = loadGoogleMaps().then((google) => {
    if (!geocoderInstance) geocoderInstance = new google.maps.Geocoder();
    return new Promise((resolve, reject) => {
      geocoderInstance.geocode(
        { location: { lat, lng }, language: 'en', region: 'in' },
        (results, status) => {
          if (status !== 'OK' || !results?.[0]) {
            reject(new Error('Could not get address'));
            return;
          }
          const top = pickBestGeocodeResult(results);
          const place = parseGeocodeResult(top, lat, lng);
          saveSpatialGeocodeCache(lat, lng, place);
          resolve(place);
        },
      );
    });
  });
  geocodeInFlight.set(cacheKey, request);
  request.finally(() => {
    if (geocodeInFlight.get(cacheKey) === request) geocodeInFlight.delete(cacheKey);
  }).catch(() => {});
  return request;
};

/**
 * The green route line (casing + core). `{ muted: true }` draws the part of a
 * trip already driven instead: one thinner grey line under the green one.
 */
export const drawRoute = async (map, path, { muted = false } = {}) => {
  if (map?.__native) {
    const points = (path || []).map(toLatLngLiteral).filter(Boolean);
    if (muted) {
      return map.addPolylines([
        { path: points, geodesic: true, strokeColor: '#8B9890', strokeOpacity: 0.9, strokeWeight: 4, zIndex: 9 },
      ]);
    }
    return map.addPolylines([
      { path: points, geodesic: true, strokeColor: '#005c38', strokeOpacity: 0.35, strokeWeight: 8, zIndex: 10 },
      { path: points, geodesic: true, strokeColor: '#00A86B', strokeOpacity: 1.0, strokeWeight: 5, zIndex: 11 },
    ]);
  }
  const google = getGoogle() || await loadGoogleMaps();
  if (muted) {
    const done = new google.maps.Polyline({
      path,
      geodesic: true,
      strokeColor: '#8B9890',
      strokeOpacity: 0.9,
      strokeWeight: 4,
      zIndex: 9,
      map,
    });
    return { setMap: (m) => done.setMap(m), getPath: () => done.getPath() };
  }
  // Dual-layer glowing route line (casing + core) for high-end Uber/Apple map aesthetics
  const casing = new google.maps.Polyline({
    path,
    geodesic: true,
    strokeColor: '#005c38',
    strokeOpacity: 0.35,
    strokeWeight: 8,
    zIndex: 10,
    map,
  });

  const core = new google.maps.Polyline({
    path,
    geodesic: true,
    strokeColor: '#00A86B',
    strokeOpacity: 1.0,
    strokeWeight: 5,
    zIndex: 11,
    map,
  });

  return {
    setMap: (m) => {
      casing.setMap(m);
      core.setMap(m);
    },
    getPath: () => core.getPath(),
  };
};

export const placeMarker = async (map, position, label, iconUrl) => {
  if (map?.__native) return map.addMarker(position, NATIVE_ICONS[label] || NATIVE_ICONS.A);
  const google = getGoogle() || await loadGoogleMaps();
  const opts = {
    map,
    position,
    optimized: true,
    label: label ? { text: label, color: '#fff', fontWeight: '700' } : undefined,
  };
  if (iconUrl) {
    opts.icon = {
      url: iconUrl,
      scaledSize: new google.maps.Size(36, 36),
      anchor: new google.maps.Point(18, 18),
    };
    delete opts.label;
  }
  return new google.maps.Marker(opts);
};

const AUTO_MARKER_SVG = encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">'
  + '<circle cx="20" cy="20" r="18" fill="#ffffff" stroke="#00A86B" stroke-width="2.5"/>'
  + '<text x="20" y="26" text-anchor="middle" font-size="18">🛺</text>'
  + '</svg>',
);

export const getAutoMarkerIcon = async () => {
  const google = await loadGoogleMaps();
  return {
    url: `data:image/svg+xml,${AUTO_MARKER_SVG}`,
    scaledSize: new google.maps.Size(40, 40),
    anchor: new google.maps.Point(20, 20),
  };
};

const SAVED_PLACE_SVG = (kind) => {
  const glyph = kind === 'home'
    ? '<path d="M12 21v-6h4v6M4 12l8-7 8 7v8a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1z" fill="none" stroke="#ffffff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>'
    : '<path d="M6 21V5a1 1 0 0 1 1-1h7a1 1 0 0 1 1 1v16M15 21V9h3a1 1 0 0 1 1 1v11M4 21h16M9 8h2M9 12h2M9 16h2" fill="none" stroke="#ffffff" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>';
  return encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="44" height="52" viewBox="0 0 44 52">'
    + '<path d="M22 51C22 51 40 33 40 20A18 18 0 1 0 4 20C4 33 22 51 22 51Z" fill="#00875a" stroke="#ffffff" stroke-width="2.5"/>'
    + `<g transform="translate(10 8)">${glyph}</g>`
    + '</svg>',
  );
};

/** Home / Work pin shown on the booking map when the user has saved that place. */
export const placeSavedPlaceMarker = (map, position, kind, title, onClick) => {
  if (map?.__native) {
    if (!position?.lat) return null;
    const marker = map.addMarker(
      { lat: position.lat, lng: position.lng },
      { ...(kind === 'home' ? NATIVE_ICONS.savedHome : NATIVE_ICONS.savedWork), zIndex: 30, title: title || '' },
    );
    if (marker && typeof onClick === 'function') marker.addListener('click', () => onClick(kind));
    return marker;
  }
  const goog = window.google;
  if (!goog?.maps?.Marker || !map || !position?.lat) return null;
  const marker = new goog.maps.Marker({
    map,
    position: { lat: position.lat, lng: position.lng },
    zIndex: 30,
    title: title || (kind === 'home' ? 'Home' : 'Work'),
    icon: {
      url: `data:image/svg+xml,${SAVED_PLACE_SVG(kind)}`,
      scaledSize: new goog.maps.Size(38, 45),
      anchor: new goog.maps.Point(19, 45),
    },
  });
  if (typeof onClick === 'function') marker.addListener('click', () => onClick(kind));
  return marker;
};

export const placeNearbyAutoMarker = (map, position) => {
  if (map?.__native) return position ? map.addMarker(position, { ...NATIVE_ICONS.auto, zIndex: 50 }) : null;
  const goog = window.google;
  if (!goog?.maps?.Marker || !map || !position) return null;
  return new goog.maps.Marker({
    map,
    position,
    zIndex: 50,
    optimized: false,
    icon: {
      path: goog.maps.SymbolPath.CIRCLE,
      scale: 14,
      fillColor: '#00A86B',
      fillOpacity: 1,
      strokeColor: '#ffffff',
      strokeWeight: 3,
    },
    label: {
      text: '🛺',
      fontSize: '15px',
      fontWeight: '700',
    },
  });
};

export const createPulsingRadar = (map, center) => {
  if (!map || !center?.lat) return null;
  if (!map.__native && !window.google?.maps) return null;
  let radius = 60;
  let opacity = 0.45;
  // Native: an in-place circle edit through the patched plugin (nativeMapHandle).
  const circle = map.__native ? map.addCircle({
    center: toLatLngLiteral(center),
    radius,
    fillColor: '#00A86B',
    fillOpacity: opacity,
    strokeColor: '#00A86B',
    strokeOpacity: opacity * 1.2,
    strokeWeight: 2,
    clickable: false,
  }) : new window.google.maps.Circle({
    map,
    center,
    radius,
    fillColor: '#00A86B',
    fillOpacity: opacity,
    strokeColor: '#00A86B',
    strokeOpacity: opacity * 1.2,
    strokeWeight: 2,
    clickable: false,
  });

  const interval = setInterval(() => {
    radius += 22;
    opacity -= 0.03;
    if (radius > 450) {
      radius = 60;
      opacity = 0.45;
    }
    circle.setRadius(radius);
    circle.setOptions({
      fillOpacity: Math.max(0, opacity),
      strokeOpacity: Math.max(0, opacity * 1.2),
    });
  }, 120);

  return {
    cleanup: () => {
      clearInterval(interval);
      circle.setMap(null);
    },
  };
};

export const calculateBearing = (start, end) => {
  if (!start || !end || (start.lat === end.lat && start.lng === end.lng)) return 0;
  const rad = (d) => (d * Math.PI) / 180;
  const lat1 = rad(start.lat);
  const lat2 = rad(end.lat);
  const dLng = rad(end.lng - start.lng);

  const y = Math.sin(dLng) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
  const brng = (Math.atan2(y, x) * 180) / Math.PI;
  return (brng + 360) % 360;
};

const buildGliderContent = () => {
  const el = document.createElement('div');
  el.style.cssText = 'width:36px;height:36px;position:relative;pointer-events:none;will-change:transform;';
  el.innerHTML = (
    '<div class="mt-glider-arrow" style="position:absolute;inset:0;display:grid;place-items:center;'
    + 'transform-origin:50% 50%;transition:transform 220ms linear;">'
    + '<svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">'
    + '<path d="M12 2 L20 21 L12 16 L4 21 Z" fill="#00A86B" stroke="#ffffff" stroke-width="1.6" stroke-linejoin="round"/>'
    + '</svg></div>'
    + '<div style="position:absolute;inset:0;display:grid;place-items:center;font-size:17px;'
    + 'filter:drop-shadow(0 1px 2px rgba(0,0,0,0.35));">🛺</div>'
  );
  return { root: el, arrow: el.querySelector('.mt-glider-arrow') };
};

// Glide duration is clamped to this even when the real gap since the last
// ping was longer — keeps a stretch of missed pings from playing as a slow
// crawl. Past GLIDE_SNAP_GAP_MS the gap is long enough (backgrounded app,
// dead zone, GPS stall) that animating it as a drive would be dishonest —
// the marker snaps straight to the real position instead.
const GLIDE_MAX_MS = 12000;
const GLIDE_SNAP_GAP_MS = 45000;

/**
 * The moving auto on the trip map. Uses AdvancedMarkerElement (GPU-composited,
 * smooth) when the map is a vector map with a mapId; falls back to the legacy
 * Marker otherwise so a build without a Map ID still shows the auto. Heading
 * rotation is applied once per GPS leg, not per animation frame.
 */
export class AnimateGliderMarker {
  constructor(map, initialPosition) {
    this.map = map;
    this.currentPos = { ...initialPosition };
    this.targetPos = { ...initialPosition };
    this.bearing = 0;
    this.animFrame = null;
    this.marker = null;
    this.arrowEl = null;
    this.legacy = false;
    this.lastUpdateAt = 0;
    // Native map: two markers (turning arrow + upright auto) glided by the
    // phone itself in one call per GPS ping, not by a JS frame loop.
    this.native = Boolean(map?.__native);
    this.init(initialPosition);
  }

  async init(position) {
    if (this.native) {
      this.arrowMarker = this.map.addMarker(position, { ...NATIVE_ICONS.gliderArrow, zIndex: 120 });
      this.autoMarker = this.map.addMarker(position, { ...NATIVE_ICONS.gliderAuto, zIndex: 121 });
      return;
    }
    const google = await loadGoogleMaps();
    const AdvancedMarker = google.maps.marker?.AdvancedMarkerElement;
    const hasMapId = Boolean(
      process.env.REACT_APP_GOOGLE_MAPS_MAP_ID
      || this.map?.get?.('mapId') || this.map?.mapId,
    );
    if (AdvancedMarker && hasMapId) {
      try {
        const { root, arrow } = buildGliderContent();
        this.arrowEl = arrow;
        this.marker = new AdvancedMarker({
          map: this.map,
          position,
          zIndex: 120,
          content: root,
        });
        return;
      } catch { /* fall through to legacy */ }
    }
    this.legacy = true;
    this.marker = new google.maps.Marker({
      map: this.map,
      position,
      zIndex: 120,
      optimized: false,
      icon: {
        path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
        scale: 6,
        fillColor: '#00A86B',
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 2,
        rotation: 0,
      },
      label: { text: '🛺', fontSize: '16px', fontWeight: '700' },
    });
  }

  applyBearing() {
    const deg = Math.round(this.bearing);
    if (this.native) return; // sent with the move in nativeGlide()
    if (this.legacy) {
      if (!this.marker?.getIcon) return;
      const icon = this.marker.getIcon();
      if (icon && typeof icon === 'object') this.marker.setIcon({ ...icon, rotation: deg });
    } else if (this.arrowEl) {
      this.arrowEl.style.transform = `rotate(${deg}deg)`;
    }
  }

  nativeGlide(pos, durationMs) {
    this.map.moveMarkers([
      { marker: this.arrowMarker, pos, rotation: Math.round(this.bearing) },
      { marker: this.autoMarker, pos },
    ], durationMs).catch(() => {});
  }

  setMarkerPosition(pos) {
    if (this.native) { this.nativeGlide(pos, 0); return; }
    if (!this.marker) return;
    if (this.legacy) this.marker.setPosition(pos);
    else this.marker.position = pos;
  }

  updatePosition(newPos, durationMs) {
    if (!newPos?.lat || !newPos?.lng) return;
    if (this.currentPos.lat === newPos.lat && this.currentPos.lng === newPos.lng) return;

    const startTime = performance.now();
    // Glide across exactly the gap since the previous GPS ping, at a constant
    // speed, so the auto looks like it's driving rather than doing
    // "glide → sit → glide". Clamp: first ping / a long GPS stall / a burst.
    const gap = this.lastUpdateAt ? startTime - this.lastUpdateAt : 3000;
    this.lastUpdateAt = startTime;

    // Heading is constant across one leg — rotate once, not every frame.
    this.bearing = calculateBearing(this.currentPos, newPos);
    this.applyBearing();

    if (this.animFrame) cancelAnimationFrame(this.animFrame);

    if (!durationMs && gap > GLIDE_SNAP_GAP_MS) {
      this.currentPos = { ...newPos };
      this.targetPos = { ...newPos };
      this.setMarkerPosition(this.currentPos);
      return;
    }

    const glideMs = durationMs || Math.min(Math.max(gap, 900), GLIDE_MAX_MS);
    this.targetPos = { ...newPos };
    const startPos = { ...this.currentPos };

    if (this.native) {
      // The phone glides from wherever the marker is now, so a new ping
      // mid-glide continues smoothly instead of jumping back.
      this.currentPos = { ...newPos };
      this.nativeGlide(this.currentPos, glideMs);
      return;
    }

    const animate = (now) => {
      const progress = Math.min((now - startTime) / glideMs, 1); // linear
      const lat = startPos.lat + (this.targetPos.lat - startPos.lat) * progress;
      const lng = startPos.lng + (this.targetPos.lng - startPos.lng) * progress;
      this.currentPos = { lat, lng };
      this.setMarkerPosition(this.currentPos);
      if (progress < 1) this.animFrame = requestAnimationFrame(animate);
    };

    this.animFrame = requestAnimationFrame(animate);
  }

  destroy() {
    if (this.animFrame) cancelAnimationFrame(this.animFrame);
    if (this.native) {
      this.arrowMarker?.setMap(null);
      this.autoMarker?.setMap(null);
      return;
    }
    if (!this.marker) return;
    if (this.legacy) this.marker.setMap(null);
    else this.marker.map = null;
  }
}

export const placeAssignedAutoMarker = async (map, position) => {
  if (map?.__native) return map.addMarker(position, { ...NATIVE_ICONS.auto, zIndex: 100 });
  const google = await loadGoogleMaps();
  return new google.maps.Marker({
    map,
    position,
    zIndex: 100,
    optimized: false,
    icon: {
      path: google.maps.SymbolPath.CIRCLE,
      scale: 14,
      fillColor: '#00A86B',
      fillOpacity: 1,
      strokeColor: '#ffffff',
      strokeWeight: 3,
    },
    label: {
      text: '🛺',
      fontSize: '16px',
      fontWeight: '700',
    },
  });
};

export const fitMapToPoints = (map, points, padding = 64, maxZoom = 16.0) => {
  if (!map || !points?.length) return;
  const validPoints = points.filter((p) => p && p.lat != null && p.lng != null);
  if (!validPoints.length) return;

  if (validPoints.length === 1) {
    fitMapToPoint(map, validPoints[0], padding, 15.8);
    return;
  }

  if (validPoints.length === 2 && distanceBetweenM(validPoints[0], validPoints[1]) < 100) {
    fitMapToPoint(map, validPoints[0], padding, 15.8);
    return;
  }

  if (map.__native) {
    map.fitPoints(validPoints, padding, maxZoom).catch(() => {});
    return;
  }

  const bounds = new window.google.maps.LatLngBounds();
  validPoints.forEach((p) => bounds.extend(p));
  const pad = typeof padding === 'number'
    ? { top: padding, bottom: padding, left: padding, right: padding }
    : padding;
  map.fitBounds(bounds, pad);

  if (window.google?.maps?.event) {
    window.google.maps.event.addListenerOnce(map, 'idle', () => {
      if (typeof map.getZoom === 'function' && map.getZoom() > maxZoom) {
        map.setZoom(maxZoom);
      }
    });
  }
};

export const getActiveRideMapPadding = ({ sheetHeight = 300 } = {}) => ({
  top: 96,
  bottom: Math.round(sheetHeight) + 56,
  left: 48,
  right: 48,
});

export const clearMarkers = (markers) => {
  markers.forEach((m) => m?.setMap(null));
  markers.length = 0;
};

const GEO_ERRORS = {
  1: 'denied',
  2: 'unavailable',
  3: 'timeout',
};

/** Single-shot position with explicit options. */
export const getCurrentPosition = async (opts = {}) => {
  await assertLocationPermission({ requestPermission: opts.requestPermission !== false });
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('unsupported'));
      return;
    }
    const {
      enableHighAccuracy = false,
      timeout = 8000,
      maximumAge = 0,
    } = opts;
    let settled = false;
    // Independent JS-level backstop — the `timeout` option below is a request
    // to the browser/WebView, not a guarantee. Observed on-device: with no
    // recent GPS fix available (e.g. indoors), getCurrentPosition can hang
    // forever with neither callback ever firing. Give the API's own timeout
    // a head start (more informative — a real 'timeout' error), then force
    // it either way so this promise can never hang the caller indefinitely.
    const hardTimer = setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(new Error('timeout'));
    }, timeout + 1500);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (settled) return;
        settled = true;
        clearTimeout(hardTimer);
        resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        });
      },
      (err) => {
        if (settled) return;
        settled = true;
        clearTimeout(hardTimer);
        reject(new Error(GEO_ERRORS[err?.code] || 'failed'));
      },
      { enableHighAccuracy, timeout, maximumAge },
    );
  });
};

/**
 * Fast fix for cold start — only used on first load, never for “My location” taps.
 */
export const getQuickPosition = () =>
  getCurrentPosition({ enableHighAccuracy: false, maximumAge: 5000, timeout: 8000, requestPermission: false });

/**
 * Near-instant coarse fix: last-known / cell / Wi-Fi position, up to 2 min old.
 * Used on cold start to land the map on the right area in ~ms while the accurate
 * race runs. Never for a "My location" tap (that wants a fresh precise fix).
 */
export const getLastKnownPosition = () =>
  getCurrentPosition({ enableHighAccuracy: false, maximumAge: 120000, timeout: 3000, requestPermission: false });

/** Real GPS single-shot query. */
export const getAccuratePosition = () =>
  getCurrentPosition({ enableHighAccuracy: true, maximumAge: 0, timeout: 15000 });

/** Check if two consecutive GPS fixes imply an impossible teleport (> 25 m/s or 90 km/h). */
export const isPlausibleGpsJump = (prev, next, maxSpeedMs = 25) => {
  if (!prev || !prev.lat || !next || !next.lat) return true;
  const distM = distanceBetweenM(prev, next);
  const dtS = Math.max(0.5, ((next.timestamp || Date.now()) - (prev.timestamp || Date.now() - 1000)) / 1000);
  const impliedSpeed = distM / dtS;
  return impliedSpeed <= maxSpeedMs;
};

/** Inverse-variance weighted fusion across rolling GPS fixes (w_i = 1 / accuracy^2). */
export const weightedGpsFuse = (fixes) => {
  if (!fixes || !fixes.length) return null;
  if (fixes.length === 1) return fixes[0];

  let wSum = 0;
  let latSum = 0;
  let lngSum = 0;
  let bestAccuracy = Infinity;

  for (const f of fixes) {
    const acc = Math.max(2, f.accuracy || 25);
    const w = 1 / (acc * acc);
    wSum += w;
    latSum += f.lat * w;
    lngSum += f.lng * w;
    if (acc < bestAccuracy) bestAccuracy = acc;
  }

  if (wSum === 0) return fixes[fixes.length - 1];

  return {
    lat: latSum / wSum,
    lng: lngSum / wSum,
    accuracy: bestAccuracy,
    timestamp: Date.now(),
  };
};

/**
 * Windowed Accuracy Race Stream — races GPS fixes over a 4-5s window.
 * Keeps a rolling ring-buffer, applies outlier rejection + inverse-variance fusion,
 * and short-circuits instantly as soon as accuracy <= 15m.
 */
export const getBestDevicePosition = async ({ maxWaitMs = 5000, targetAccuracyM = 15, requestPermission = true } = {}) => {
  await assertLocationPermission({ requestPermission });
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('unsupported'));
      return;
    }

    const startedAt = Date.now();
    const ringBuffer = [];
    let bestFix = null;
    let watchId = null;
    let timer = null;
    let lastFix = null;
    let finished = false;

    const finish = (err) => {
      if (finished) return;
      finished = true;
      if (watchId != null) {
        navigator.geolocation.clearWatch(watchId);
        watchId = null;
      }
      clearTimeout(timer);
      const elapsedMs = Date.now() - startedAt;

      if (ringBuffer.length > 0) {
        const fused = weightedGpsFuse(ringBuffer);
        const result = fused || bestFix;
        logAnalyticsEvent('gps_fix_quality', {
          accuracy_meters: Math.round(result.accuracy || 0),
          best_raw_accuracy: Math.round(bestFix?.accuracy || 0),
          sample_count: ringBuffer.length,
          elapsed_ms: elapsedMs,
          used_fallback: false,
        });
        resolve(result);
      } else if (bestFix) {
        logAnalyticsEvent('gps_fix_quality', {
          accuracy_meters: Math.round(bestFix.accuracy || 0),
          best_raw_accuracy: Math.round(bestFix.accuracy || 0),
          sample_count: 1,
          elapsed_ms: elapsedMs,
          used_fallback: false,
        });
        resolve(bestFix);
      } else {
        logAnalyticsEvent('gps_fix_quality', {
          accuracy_meters: 0,
          best_raw_accuracy: 0,
          sample_count: 0,
          elapsed_ms: elapsedMs,
          used_fallback: true,
        });
        reject(err || new Error('timeout'));
      }
    };

    timer = setTimeout(() => finish(), maxWaitMs);

    watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const raw = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy ?? 9999,
          timestamp: pos.timestamp || Date.now(),
        };

        // Outlier speed filter
        if (lastFix && !isPlausibleGpsJump(lastFix, raw)) {
          return;
        }
        lastFix = raw;

        if (!bestFix || raw.accuracy < bestFix.accuracy) {
          bestFix = raw;
        }

        ringBuffer.push(raw);
        if (ringBuffer.length > 4) ringBuffer.shift();

        if (raw.accuracy <= targetAccuracyM) {
          finish();
        }
      },
      (err) => {
        if (err?.code === 1) {
          finish(new Error('denied'));
        }
        // Note: For transient errors like kCLErrorLocationUnknown (code 2), we let the window timer run
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: maxWaitMs },
    );
  });
};

/** First load — uses streaming window with 5s cap, falls back to quick position. Rejects if GPS unavailable. */
export const getDevicePosition = () =>
  getBestDevicePosition({ maxWaitMs: 5000, targetAccuracyM: 20, requestPermission: false })
    .catch(() => getQuickPosition());

/** Convert latitude to spherical Mercator Y coordinate in world pixels at given zoom. */
export const latToMercatorY = (lat, zoom) => {
  const sin = Math.sin((lat * Math.PI) / 180);
  const clampedSin = Math.min(Math.max(sin, -0.9999), 0.9999);
  const scale = 256 * Math.pow(2, zoom);
  return (0.5 - Math.log((1 + clampedSin) / (1 - clampedSin)) / (4 * Math.PI)) * scale;
};

/** Convert spherical Mercator Y coordinate to latitude at given zoom. */
export const mercatorYToLat = (y, zoom) => {
  const scale = 256 * Math.pow(2, zoom);
  const yNorm = 0.5 - y / scale;
  return 90 - (360 / Math.PI) * Math.atan(Math.exp(-yNorm * 2 * Math.PI));
};

/** Calculate the camera center LatLng such that `pos` appears at the visible center of the padded area above the sheet. */
export const calculateCameraCenterForPoint = (pos, padding, zoom = 16.8) => {
  if (!pos?.lat || !pos?.lng) return pos;
  const top = Number(padding?.top) || 56;
  const bottom = Number(padding?.bottom) || 0;
  // deltaY = vertical pixel shift down so `pos` appears in the center of the area above the sheet
  const deltaY = (bottom - top) / 2;
  const y = latToMercatorY(pos.lat, zoom) + deltaY;
  const targetLat = mercatorYToLat(y, zoom);
  return { lat: targetLat, lng: pos.lng };
};

export const fitMapToPoint = (map, pos, padding = 64, targetZoom = 16.8) => {
  if (!map || pos?.lat == null || pos?.lng == null) return;
  if (map.__native) {
    map.moveCamera(pos, targetZoom, padding).catch(() => {});
    return;
  }
  const google = getGoogle();
  if (!google) {
    map.panTo(pos);
    return;
  }

  // Set zoom to crisp, high-clarity street level
  if (typeof map.getZoom === 'function') {
    const currentZoom = map.getZoom();
    if (!currentZoom || currentZoom < targetZoom - 1 || currentZoom > targetZoom + 1.5) {
      map.setZoom(targetZoom);
    }
  }

  const zoom = typeof map.getZoom === 'function' ? (map.getZoom() || targetZoom) : targetZoom;
  const cameraCenter = (padding && typeof padding === 'object')
    ? calculateCameraCenterForPoint(pos, padding, zoom)
    : pos;

  if (typeof map.panTo === 'function') {
    map.panTo(cameraCenter);
  } else if (typeof map.setCenter === 'function') {
    map.setCenter(cameraCenter);
  }
};

/** Refine GPS in background with rolling fusion without blocking UI. */
export const refinePosition = (onRefined, { maxMs = 8000, targetAccuracyM = 15 } = {}) => {
  if (!navigator.geolocation || typeof onRefined !== 'function') return () => { };
  const ringBuffer = [];
  let stopped = false;
  let lastFix = null;
  let watchId = null;
  let timer = null;

  const stop = () => {
    if (stopped) return;
    stopped = true;
    if (watchId != null) {
      navigator.geolocation.clearWatch(watchId);
      watchId = null;
    }
    clearTimeout(timer);
  };

  watchId = navigator.geolocation.watchPosition(
    (pos) => {
      const raw = {
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy: pos.coords.accuracy ?? 9999,
        timestamp: pos.timestamp || Date.now(),
      };

      if (lastFix && !isPlausibleGpsJump(lastFix, raw)) {
        return;
      }
      lastFix = raw;

      ringBuffer.push(raw);
      if (ringBuffer.length > 4) ringBuffer.shift();

      const fused = weightedGpsFuse(ringBuffer);
      if (fused) {
        onRefined(fused);
      }

      if (raw.accuracy <= targetAccuracyM) {
        stop();
      }
    },
    () => { },
    { enableHighAccuracy: true, maximumAge: 0, timeout: maxMs },
  );

  timer = setTimeout(stop, maxMs);
  return stop;
};

/** Translucent accuracy confidence circle overlay (Uber / Google Maps style). */
export const createUserAccuracyCircle = (map, center, accuracy = 40) => {
  if (map?.__native) {
    if (!center?.lat) return null;
    const circle = map.addCircle({
      center: toLatLngLiteral(center),
      radius: Math.max(10, Math.min(120, accuracy)),
      fillColor: '#00A86B',
      fillOpacity: 0.1,
      strokeColor: '#00A86B',
      strokeOpacity: 0.35,
      strokeWeight: 1.5,
      clickable: false,
      zIndex: 5,
    });
    return {
      update: (newCenter, newAccuracy) => {
        if (newCenter?.lat) circle.setCenter(newCenter);
        if (newAccuracy != null) circle.setRadius(Math.max(10, Math.min(120, newAccuracy)));
      },
      destroy: () => circle.setMap(null),
    };
  }
  const google = getGoogle();
  if (!map || !google || !center?.lat) return null;

  const circle = new google.maps.Circle({
    map,
    center,
    radius: Math.max(10, Math.min(120, accuracy)),
    fillColor: '#00A86B',
    fillOpacity: 0.1,
    strokeColor: '#00A86B',
    strokeOpacity: 0.35,
    strokeWeight: 1.5,
    clickable: false,
    zIndex: 5,
  });

  return {
    update: (newCenter, newAccuracy) => {
      if (newCenter?.lat) circle.setCenter(newCenter);
      if (newAccuracy != null) {
        circle.setRadius(Math.max(10, Math.min(120, newAccuracy)));
      }
    },
    destroy: () => {
      circle.setMap(null);
    },
  };
};

/** Align map center with fixed pin above bottom sheet (Rapido-style). */
export const applyMapSheetPadding = (map, { bottom = 0, top = 56 } = {}) => {
  if (!map?.setPadding) return;
  map.setPadding({
    top,
    bottom: Math.max(0, Math.round(bottom)),
    left: 12,
    right: 12,
  });
};

/** Calculate the exact GPS coordinate located under the visual floating center pin above the bottom sheet. */
export const calculatePointUnderPin = (cameraCenter, padding, zoom = 16.8) => {
  if (!cameraCenter?.lat || !cameraCenter?.lng) return cameraCenter;
  const top = Number(padding?.top) || 56;
  const bottom = Number(padding?.bottom) || 0;
  // Visual pin is shifted up from camera center by deltaY = (bottom - top) / 2
  const deltaY = (bottom - top) / 2;
  const y = latToMercatorY(cameraCenter.lat, zoom) - deltaY;
  const targetLat = mercatorYToLat(y, zoom);
  return { lat: targetLat, lng: cameraCenter.lng };
};

export const getMapCenterLatLng = (map) => {
  const c = map?.getCenter?.();
  if (!c) return null;
  return { lat: c.lat(), lng: c.lng() };
};

let projectionOverlayInstance = null;

export const getProjectionOverlay = (map) => {
  if (!map || map.__native) return null;
  const google = window.google;
  if (!google?.maps?.OverlayView) return null;
  try {
    if (!projectionOverlayInstance || projectionOverlayInstance.getMap() !== map) {
      class HelperOverlay extends google.maps.OverlayView {
        onAdd() {}
        draw() {}
        onRemove() {}
      }
      projectionOverlayInstance = new HelperOverlay();
      projectionOverlayInstance.setMap(map);
    }
  } catch (e) {
    console.warn('HelperOverlay init error:', e);
  }
  return projectionOverlayInstance;
};

/**
 * Get the EXACT geographic coordinate located under the visual needle tip of the center pin.
 * Uses Google Maps OverlayView pixel-to-coordinate projection with zero offset distortion.
 */
export const getPointUnderVisualPin = (map, pinSelector = '.rapido-center-pin', getPadding = null) => {
  if (!map) return null;
  if (map.__native) {
    // No OverlayView on the native map: convert the pin tip's pixel with the
    // last camera stop, zoom and map padding (nativeMapUtils.containerPixelToLatLng).
    const pinEl = document.querySelector(pinSelector);
    const mapDiv = map.getDiv?.();
    if (pinEl && mapDiv) {
      const pinRect = pinEl.getBoundingClientRect();
      const mapRect = mapDiv.getBoundingClientRect();
      const point = map.containerPixelToLatLng(
        (pinRect.left + pinRect.right) / 2 - mapRect.left,
        pinRect.bottom - mapRect.top,
      );
      if (point) return point;
    }
    return getMapCenterLatLng(map);
  }
  const google = window.google;

  try {
    const pinEl = document.querySelector(pinSelector);
    const mapDiv = map.getDiv ? map.getDiv() : document.querySelector('.rapido-map');
    const overlay = getProjectionOverlay(map);
    const projection = overlay?.getProjection?.();

    if (pinEl && mapDiv && projection && google?.maps?.Point) {
      const pinRect = pinEl.getBoundingClientRect();
      const mapRect = mapDiv.getBoundingClientRect();

      // The exact tip of the needle: horizontal center and bottom edge of .rapido-center-pin
      const pinX = (pinRect.left + pinRect.right) / 2 - mapRect.left;
      const pinY = pinRect.bottom - mapRect.top;

      const latLng = projection.fromContainerPixelToLatLng(new google.maps.Point(pinX, pinY));
      if (latLng && typeof latLng.lat === 'function' && typeof latLng.lng === 'function') {
        const lat = latLng.lat();
        const lng = latLng.lng();
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          return { lat, lng };
        }
      }
    }
  } catch (e) {
    console.warn('Overlay projection calculation fallback:', e);
  }

  // Fallback to mathematical Mercator calculation if overlay is pending
  const center = getMapCenterLatLng(map);
  if (!center) return null;
  const padding = typeof getPadding === 'function' ? getPadding() : { top: 56, bottom: 220 };
  const zoom = typeof map.getZoom === 'function' ? (map.getZoom() || 16.8) : 16.8;
  return calculatePointUnderPin(center, padding, zoom);
};

/**
 * Keep fixed center-pin over the same lat/lng while zooming.
 * Geocode / move selection accurately under the pin tip on dragend.
 */
export const bindPinnedCenterPin = (map, {
  getPadding,
  onCommitCenter,
  onDragStart,
  onDragEnd,
} = {}) => {
  if (!map) return () => {};

  // Ensure helper projection overlay is mounted
  getProjectionOverlay(map);

  let dragging = false;

  const getPointUnderPin = () => {
    return getPointUnderVisualPin(map, '.rapido-center-pin', getPadding);
  };

  const notifyCenter = () => {
    const point = getPointUnderPin();
    if (point) onCommitCenter?.(point);
  };

  const dragStartListener = map.addListener('dragstart', () => {
    dragging = true;
    onDragStart?.();
  });

  const dragEndListener = map.addListener('dragend', () => {
    dragging = false;
    onDragEnd?.();
    notifyCenter();
  });

  return () => {
    if (map.__native) {
      dragStartListener?.remove?.();
      dragEndListener?.remove?.();
      return;
    }
    const maps = window.google?.maps;
    if (!maps?.event) return;
    maps.event.removeListener(dragStartListener);
    maps.event.removeListener(dragEndListener);
  };
};

/**
 * Runs `cb` once, when the map's camera next comes to rest after a
 * programmatic move. Works for the JS and the native map (the native map is
 * not a google.maps object, so google.maps.event would never fire for it).
 * A fallback timer makes sure it runs even if the camera did not move.
 */
export const onceMapIdle = (map, cb, fallbackMs = 1200) => {
  let done = false;
  const fire = () => { if (!done) { done = true; cb(); } };
  if (map?.__native) {
    map.addListenerOnce('idle', fire);
  } else if (window.google?.maps?.event && map) {
    window.google.maps.event.addListenerOnce(map, 'idle', fire);
  } else {
    fallbackMs = Math.min(fallbackMs, 400);
  }
  window.setTimeout(fire, fallbackMs);
};

export const isBillingError = (message = '') =>
  message.includes('BillingNotEnabled') || message.includes('billing');

export const getGeoErrorKey = (message = '') => {
  if (message === 'denied') return 'book.geoDenied';
  if (message === 'timeout') return 'book.geoTimeout';
  if (message === 'unavailable' || message === 'unsupported') return 'book.geoUnavailable';
  return 'book.locationError';
};
